import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ArrowLeft, ShieldCheck, Mail, Smartphone, KeyRound, Copy, Check } from "lucide-react";
import { ASSETS } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3.5 text-[14px] font-medium text-black bg-white border-2 border-black/10 rounded-xl focus:border-[#0fb013] focus:outline-none transition-colors duration-200";
const btnCls =
  "w-full inline-flex items-center justify-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-6 py-3.5 rounded-full transition-colors duration-200 shadow-[0_5px_16px_rgba(15,232,20,0.35)] disabled:opacity-60 disabled:cursor-not-allowed";

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span data-testid="login-clock" className="font-semibold text-black/55 tabular-nums">
      {now.toLocaleDateString("es-PA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {now.toLocaleTimeString("es-PA", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </span>
  );
};

const Shell = ({ children, subtitle }) => (
  <div className="font-montserrat min-h-screen bg-[#fafcf9] flex flex-col">
    <header className="bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]">
      <div className="max-w-md mx-auto px-5 flex items-center justify-between h-[68px]">
        <Link to="/"><img src="/logos/rapidpay-color.webp" alt="RapidPay" data-testid="login-header-logo" className="h-8 w-auto" /></Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-black/60 hover:text-black transition-colors">
          <ArrowLeft size={14} /> Volver al sitio
        </Link>
      </div>
    </header>
    <main className="flex-1 flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white border border-black/[0.06] rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between mb-5">
            <img src="/logos/rapidpay-color.webp" alt="RapidPay" data-testid="login-card-logo" className="h-9 w-auto" />
            <div className="w-11 h-11 rounded-2xl bg-black flex items-center justify-center">
              <Lock size={18} className="text-[#0fe814]" />
            </div>
          </div>
          <h1 className="font-extrabold text-[22px] text-black">Panel Administrativo</h1>
          {subtitle && <p className="mt-1 text-[13px] font-medium text-black/50">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        <p className="mt-5 text-center text-[11px] font-medium text-black/35">
          Acceso restringido · Protegido con autenticación en dos pasos
        </p>
        <footer data-testid="login-footer" className="mt-6 flex flex-col items-center gap-3 text-[11px]">
          <LiveClock />
          <div className="flex items-center gap-2 text-black/40 font-semibold">
            <span>Seguridad certificada por</span>
            <img src="/logos/entrust.webp" alt="Entrust" data-testid="login-entrust-logo" className="h-6 w-auto" />
          </div>
        </footer>
      </div>
    </main>
  </div>
);

const ErrorMsg = ({ msg }) =>
  msg ? (
    <p data-testid="auth-error" className="mt-3 text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-2.5 rounded-xl">
      {msg}
    </p>
  ) : null;

const AdminAuthFlow = ({ onAuthenticated }) => {
  const [stage, setStage] = useState("login"); // login | change_password | setup_totp | 2fa
  const [tempToken, setTempToken] = useState("");
  const [methods, setMethods] = useState([]);
  const [error, setError] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [method2fa, setMethod2fa] = useState("totp");

  const handleStageResponse = async (data) => {
    setError("");
    setCode("");
    if (data.stage === "done") {
      onAuthenticated(data.token, data.user);
      return;
    }
    setTempToken(data.temp_token);
    if (data.stage === "change_password") setStage("change_password");
    if (data.stage === "setup_totp") {
      setStage("setup_totp");
      await loadQr(data.temp_token);
    }
    if (data.stage === "2fa") {
      setMethods(data.methods || []);
      setMethod2fa(data.preferred || ((data.methods || []).includes("totp") ? "totp" : "email"));
      setStage("2fa");
    }
  };

  const loadQr = async (token) => {
    try {
      const res = await axios.post(`${API}/auth/setup-totp`, { temp_token: token });
      setQr(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error generando el código QR");
    }
  };

  const call = async (fn) => {
    setLoading(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.response?.data?.detail || "Error de conexión. Intenta de nuevo");
    } finally {
      setLoading(false);
    }
  };

  const doLogin = () =>
    call(async () => {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      await handleStageResponse(res.data);
    });

  const doChangePassword = () =>
    call(async () => {
      if (newPassword.length < 8) throw { response: { data: { detail: "La contraseña debe tener al menos 8 caracteres" } } };
      if (newPassword !== newPassword2) throw { response: { data: { detail: "Las contraseñas no coinciden" } } };
      const res = await axios.post(`${API}/auth/change-password`, { temp_token: tempToken, new_password: newPassword });
      await handleStageResponse(res.data);
    });

  const doVerifyTotpSetup = () =>
    call(async () => {
      const res = await axios.post(`${API}/auth/verify-totp-setup`, { temp_token: tempToken, code: code.trim() });
      await handleStageResponse(res.data);
    });

  const doSendEmailOtp = () =>
    call(async () => {
      const res = await axios.post(`${API}/auth/send-email-otp`, { temp_token: tempToken });
      if (res.data.fallback === "setup_totp") {
        setTempToken(res.data.temp_token);
        setStage("setup_totp");
        setError("No se pudo enviar el correo. Configura Google Authenticator para continuar");
        await loadQr(res.data.temp_token);
        return;
      }
      setEmailSent(true);
    });

  const doVerify2fa = () =>
    call(async () => {
      const res = await axios.post(`${API}/auth/verify-2fa`, { temp_token: tempToken, method: method2fa, code: code.trim() });
      await handleStageResponse(res.data);
    });

  /* ---------- LOGIN ---------- */
  if (stage === "login") {
    return (
      <Shell subtitle="Ingresa con tu correo y contraseña">
        <form onSubmit={(e) => { e.preventDefault(); doLogin(); }} className="space-y-4">
          <input data-testid="login-email" type="email" className={inputCls} placeholder="Correo electrónico" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <input data-testid="login-password" type="password" className={inputCls} placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="login-submit" type="submit" disabled={loading || !email || !password} className={btnCls}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <KeyRound size={16} />}
            Iniciar sesión
          </button>
        </form>
        <ErrorMsg msg={error} />
        <div className="mt-4 text-center">
          <button
            type="button"
            data-testid="forgot-password-btn"
            disabled={forgotBusy}
            onClick={async () => {
              if (!email) { setError("Escribe tu correo para restablecer la contraseña"); return; }
              setForgotBusy(true); setError(""); setForgotMsg("");
              try {
                const res = await axios.post(`${API}/auth/forgot-password`, { email });
                setForgotMsg(res.data.detail);
              } catch (e) {
                const d = e.response?.data?.detail;
                setError(typeof d === "string" ? d : "No se pudo procesar la solicitud");
              } finally { setForgotBusy(false); }
            }}
            className="text-[12px] font-bold text-black/55 hover:text-[#0fb013] underline disabled:opacity-50"
          >
            {forgotBusy ? "Enviando…" : "¿Olvidaste tu contraseña? Restablecer por correo"}
          </button>
          {forgotMsg && <p data-testid="forgot-password-msg" className="mt-3 text-[12px] font-bold text-[#0fb013] bg-[#0fe814]/10 px-4 py-2.5 rounded-xl">{forgotMsg}</p>}
        </div>
      </Shell>
    );
  }

  /* ---------- CAMBIO DE CONTRASEÑA ---------- */
  if (stage === "change_password") {
    return (
      <Shell subtitle="Primer ingreso: crea tu nueva contraseña">
        <form onSubmit={(e) => { e.preventDefault(); doChangePassword(); }} className="space-y-4">
          <input data-testid="new-password" type="password" className={inputCls} placeholder="Nueva contraseña (mín. 8 caracteres)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />
          <input data-testid="new-password-2" type="password" className={inputCls} placeholder="Repite la nueva contraseña" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} />
          <button data-testid="change-password-submit" type="submit" disabled={loading || !newPassword || !newPassword2} className={btnCls}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={16} />}
            Guardar y continuar
          </button>
        </form>
        <ErrorMsg msg={error} />
        <div className="mt-4 text-center">
          <button
            type="button"
            data-testid="forgot-password-btn"
            disabled={forgotBusy}
            onClick={async () => {
              if (!email) { setError("Escribe tu correo para restablecer la contraseña"); return; }
              setForgotBusy(true); setError(""); setForgotMsg("");
              try {
                const res = await axios.post(`${API}/auth/forgot-password`, { email });
                setForgotMsg(res.data.detail);
              } catch (e) {
                const d = e.response?.data?.detail;
                setError(typeof d === "string" ? d : "No se pudo procesar la solicitud");
              } finally { setForgotBusy(false); }
            }}
            className="text-[12px] font-bold text-black/55 hover:text-[#0fb013] underline disabled:opacity-50"
          >
            {forgotBusy ? "Enviando…" : "¿Olvidaste tu contraseña? Restablecer por correo"}
          </button>
          {forgotMsg && <p data-testid="forgot-password-msg" className="mt-3 text-[12px] font-bold text-[#0fb013] bg-[#0fe814]/10 px-4 py-2.5 rounded-xl">{forgotMsg}</p>}
        </div>
      </Shell>
    );
  }

  /* ---------- CONFIGURAR GOOGLE AUTHENTICATOR ---------- */
  if (stage === "setup_totp") {
    return (
      <Shell subtitle="Configura la verificación en dos pasos">
        <div className="space-y-4">
          <div className="bg-[#fafcf9] border border-black/[0.06] rounded-2xl p-4 text-center">
            <p className="text-[12px] font-semibold text-black/60 mb-3">
              1. Escanea este código con <strong>Google Authenticator</strong> (o Authy, 1Password, etc.)
            </p>
            {qr ? (
              <img data-testid="totp-qr" src={qr.qr_base64} alt="Código QR TOTP" className="mx-auto w-44 h-44 rounded-xl border border-black/10" />
            ) : (
              <div className="mx-auto w-44 h-44 rounded-xl border border-black/10 flex items-center justify-center">
                <Loader2 size={22} className="animate-spin text-black/30" />
              </div>
            )}
            {qr && (
              <button
                type="button"
                data-testid="copy-secret"
                onClick={() => { navigator.clipboard?.writeText(qr.secret); setSecretCopied(true); setTimeout(() => setSecretCopied(false), 2000); }}
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold text-black/50 hover:text-black bg-black/[0.04] px-3 py-1.5 rounded-full transition-colors"
              >
                {secretCopied ? <Check size={12} className="text-[#0fb013]" /> : <Copy size={12} />}
                {secretCopied ? "Copiado" : `Clave manual: ${qr.secret}`}
              </button>
            )}
          </div>
          <p className="text-[12px] font-semibold text-black/60">2. Ingresa el código de 6 dígitos generado por la app:</p>
          <form onSubmit={(e) => { e.preventDefault(); doVerifyTotpSetup(); }} className="space-y-4">
            <input
              data-testid="totp-setup-code"
              inputMode="numeric"
              maxLength={6}
              className={`${inputCls} text-center text-[22px] tracking-[10px] font-bold`}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />
            <button data-testid="totp-setup-submit" type="submit" disabled={loading || code.length !== 6} className={btnCls}>
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Smartphone size={16} />}
              Activar y entrar
            </button>
          </form>
          <ErrorMsg msg={error} />
        </div>
      </Shell>
    );
  }

  /* ---------- VERIFICACIÓN 2FA ---------- */
  return (
    <Shell subtitle="Verificación en dos pasos">
      <div className="space-y-4">
        {methods.length > 1 && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="method-totp"
              onClick={() => { setMethod2fa("totp"); setError(""); }}
              className={`flex items-center justify-center gap-2 text-[12px] font-bold px-4 py-3 rounded-xl border-2 transition-colors ${method2fa === "totp" ? "border-[#0fb013] bg-[#0fe814]/10 text-black" : "border-black/10 text-black/50 hover:border-black/30"}`}
            >
              <Smartphone size={15} /> Authenticator
            </button>
            <button
              type="button"
              data-testid="method-email"
              onClick={() => { setMethod2fa("email"); setError(""); }}
              className={`flex items-center justify-center gap-2 text-[12px] font-bold px-4 py-3 rounded-xl border-2 transition-colors ${method2fa === "email" ? "border-[#0fb013] bg-[#0fe814]/10 text-black" : "border-black/10 text-black/50 hover:border-black/30"}`}
            >
              <Mail size={15} /> Código por correo
            </button>
          </div>
        )}

        {method2fa === "email" && (
          <button type="button" data-testid="send-email-otp" onClick={doSendEmailOtp} disabled={loading} className="w-full text-[12px] font-bold text-[#0fb013] bg-[#0fe814]/10 hover:bg-[#0fe814]/20 px-4 py-3 rounded-xl transition-colors">
            {emailSent ? "Código enviado — revisa tu correo (reenviar)" : "Enviar código a mi correo"}
          </button>
        )}

        <p className="text-[12px] font-semibold text-black/60">
          {method2fa === "totp" ? "Ingresa el código de tu app Authenticator:" : "Ingresa el código de 6 dígitos recibido por correo:"}
        </p>
        <form onSubmit={(e) => { e.preventDefault(); doVerify2fa(); }} className="space-y-4">
          <input
            data-testid="code-2fa"
            inputMode="numeric"
            maxLength={6}
            className={`${inputCls} text-center text-[22px] tracking-[10px] font-bold`}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
          />
          <button data-testid="verify-2fa-submit" type="submit" disabled={loading || code.length !== 6} className={btnCls}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={16} />}
            Verificar y entrar
          </button>
        </form>
        <ErrorMsg msg={error} />
      </div>
    </Shell>
  );
};

export default AdminAuthFlow;
